import { formatGiftBalance } from '../../../../components/horasRegalo/giftBalance';
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import Loading from '../../../../components/Loading';
import { useAuth } from '../../../../context/AuthContext';
import { useTheme } from '../../../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { TierAvatar, getUserRingTier, getProfessionColor, TIER_CONFIGS, getDynamicTierLabel, getDynamicTierIcon } from '../../../../utils/userTier';
import {
    Cita,
    fetchCitasPorUsuarioCompleto,
    fetchPrecioPlanesServicios,
    PrecioPlanServicio
} from '../../../../services/citasService';
import {
    fetchCitaCorrespondenciaPorCita,
} from '../../../../services/cita-correspondencia';
import {
    Activity, CheckCircle, Clock as ClockIcon,
    XCircle, AlertCircle, Users, Crown, TrendingUp,
    Calendar, DollarSign, Phone, Target,
    Search, ExternalLink, Wifi,
    Mail, MessageSquare, History,
    List, MapPin, CalendarCheck, CalendarPlus, ChevronRight, ChevronLeft,
    ArrowRight, Sparkles, Shield, CreditCard, Gift, Clock,
    Coffee, Copy, Check, Ticket
} from 'lucide-react';
import { isReservationCancelled } from '../../../../utils/reservationStatus';
import PagosModalHistorial from '../../../Admin/pagos/PagosModalHistorial';
import HistorialCorrespondenciaUsuarios from '../Correspondencia/HistorialCorrespondenciaUsuarios';
import { useWebSocketCitas } from '../../../../hooks/useWebSocket';
import { ROUTES } from '../../../../routes/routePaths';
import comarcaLogo from '../../../../assets/images/logo/logo.png';
import { fetchMiAfiliacion } from '../../../../services/afiliadosService';
import { fetchUsoHorasRegaloPorCliente } from '../../../../services/usarHorasDeRegaloService';


// ── Design tokens oficiales Co Marca ──
const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
};

// ── Constante de paginación ──
const ITEMS_POR_PAGINA = 5;

// ── Chip SVG dorado detallado (tarjeta digital) ──
const ChipSVG = () => (
    <svg width="50" height="36" viewBox="0 0 50 36" fill="none" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}>
        <rect x="1" y="1" width="48" height="34" rx="6" stroke="rgba(255,217,124,0.6)" strokeWidth="1.5" fill="url(#chipGrad)" />
        <rect x="14" y="9" width="22" height="18" rx="4" stroke="rgba(212,168,67,0.75)" strokeWidth="0.9" fill="rgba(255,217,124,0.18)" />
        <line x1="1" y1="13" x2="14" y2="13" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <line x1="1" y1="23" x2="14" y2="23" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <line x1="36" y1="13" x2="49" y2="13" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <line x1="36" y1="23" x2="49" y2="23" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <line x1="25" y1="1" x2="25" y2="9" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <line x1="25" y1="27" x2="25" y2="35" stroke="rgba(212,168,67,0.7)" strokeWidth="0.9" />
        <defs>
            <linearGradient id="chipGrad" x1="0" y1="0" x2="50" y2="36">
                <stop offset="0%" stopColor="#FFE49E" />
                <stop offset="35%" stopColor="#F5CB65" />
                <stop offset="75%" stopColor="#D4A843" />
                <stop offset="100%" stopColor="#B38728" />
            </linearGradient>
        </defs>
    </svg>
);

// ── Tarjeta Digital Co Marca (Hero Amplio & Alto) ──
function CoMarcaCard({ user, stats, tierConfig, profession, initials, isDark, horasGratis }: {
    user: any;
    stats: { totalCitas: number; totalPagado: number; deudaTotal: number; totalCitasValor: number; totalCitasTitular: number; totalCitasAsociadas: number };
    tierConfig: any;
    profession: { color: string; label: string };
    initials: string;
    isDark: boolean;
    horasGratis: number | null;
}) {
    const [hovered, setHovered] = useState(false);
    const holderName = user ? `${user.first_name || ''} ${user.last_name || ''}`.toUpperCase().trim() : 'MIEMBRO CO MARCA';

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                position: 'relative',
                width: '100%',
                minHeight: 'clamp(320px, 38vw, 380px)',
                borderRadius: '28px',
                overflow: 'hidden',
                background: isDark
                    ? `linear-gradient(135deg, #130724 0%, #250b3e 25%, #491666 55%, #7a1d68 80%, #9e236e 100%)`
                    : `linear-gradient(135deg, #240c3d 0%, #3e1452 30%, #5f1f6d 65%, #8f2776 100%)`,
                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : '1.5px solid rgba(255, 217, 124, 0.35)',
                boxShadow: hovered
                    ? '0 24px 64px rgba(97, 37, 123, 0.5), 0 8px 32px rgba(193, 52, 139, 0.35)'
                    : '0 14px 44px rgba(97, 37, 123, 0.35), 0 4px 18px rgba(0, 0, 0, 0.3)',
                transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
                transition: 'all 0.35s cubic-bezier(.16,1,.3,1)',
                cursor: 'default',
                animation: 'sectionFadeUp 0.6s cubic-bezier(.16,1,.3,1) both',
                fontFamily: "'DM Sans', sans-serif",
                padding: 'clamp(26px, 4vw, 38px) clamp(20px, 4.5vw, 44px)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 'clamp(20px, 3vw, 28px)',
            }}
        >
            {/* Fondo decorativo — ondas abstractas de luz */}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none',
                background: `
                    radial-gradient(ellipse 70% 50% at 85% 15%, rgba(255, 217, 124, 0.16) 0%, transparent 60%),
                    radial-gradient(ellipse 60% 70% at 10% 90%, rgba(193, 52, 139, 0.22) 0%, transparent 55%),
                    radial-gradient(circle at 50% 120%, rgba(234, 132, 6, 0.12) 0%, transparent 50%)
                `,
            }} />

            {/* Guilloche / Líneas de seguridad de tarjeta */}
            <div style={{
                position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.08,
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0, rgba(255,255,255,0.4) 1px, transparent 0, transparent 20px)',
            }} />

            {/* Shimmer diagonal al hacer hover */}
            <div style={{
                position: 'absolute', top: 0, left: hovered ? '150%' : '-100%',
                width: '60%', height: '100%',
                background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.08) 45%, rgba(255,217,124,0.15) 50%, transparent 65%)',
                transform: 'skewX(-25deg)',
                transition: 'left 1s cubic-bezier(.16,1,.3,1)',
                pointerEvents: 'none',
            }} />

            {/* ══ 1. FILA SUPERIOR: Logo Grande + Badges + NFC (Totalmente Responsivo) ══ */}
            <div style={{
                position: 'relative', zIndex: 1,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: '14px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <img
                        src={comarcaLogo}
                        alt="Co Marca Group"
                        style={{
                            height: 'clamp(38px, 4.8vw, 48px)',
                            filter: 'brightness(0) invert(1)',
                            opacity: 0.98,
                        }}
                    />
                    <div style={{ width: '1.5px', height: '28px', background: 'rgba(255,255,255,0.25)' }} />
                    <span style={{
                        fontSize: '0.76rem', fontWeight: '800',
                        color: '#FFD97C', letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                    }}>
                        TARJETA DIGITAL VIP • CO MARCA GROUP
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        background: 'rgba(74, 222, 128, 0.12)', border: '1px solid rgba(74, 222, 128, 0.35)',
                        borderRadius: '100px', padding: '4px 12px',
                        fontSize: '0.68rem', fontWeight: '800', color: '#86efac',
                        letterSpacing: '0.06em',
                    }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#4ade80', display: 'inline-block', boxShadow: '0 0 6px #4ade80' }} />
                        ACTIVA
                    </span>

                    <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        background: `${profession.color}25`,
                        border: `1.5px solid ${profession.color}50`,
                        borderRadius: '100px', padding: '4px 14px',
                        fontSize: '0.72rem', fontWeight: '800',
                        color: '#ffffff',
                    }}>
                        {getDynamicTierIcon(tierConfig) && <>{getDynamicTierIcon(tierConfig)} </>}{getDynamicTierLabel(user, tierConfig)} • {profession.label}
                    </span>

                    <div style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Wifi size={18} color="#FFD97C" style={{ transform: 'rotate(90deg)' }} />
                    </div>
                </div>
            </div>

            {/* ══ 2. FILA CENTRAL: Titular a la Izquierda + Panel Financiero Glass a la Derecha ══ */}
            <div style={{
                position: 'relative', zIndex: 1,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: '22px',
            }}>
                {/* Izquierda: Titular de la Tarjeta */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap', flex: '1 1 280px' }}>
                    <TierAvatar
                        user={user}
                        size={64}
                        initial={initials}
                        fotoUrl={(user as any)?.foto || (user as any)?.avatar || null}
                        ringThickness={3.5}
                    />
                    <div>
                        <p style={{
                            fontSize: '0.65rem', fontWeight: '700', color: 'rgba(255, 255, 255, 0.55)',
                            textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: '4px',
                        }}>
                            TITULAR DE LA TARJETA / CARDHOLDER
                        </p>
                        <h2 style={{
                            fontSize: 'clamp(1.25rem, 2.6vw, 1.8rem)', fontWeight: '900',
                            color: '#ffffff', letterSpacing: '0.04em', margin: 0, lineHeight: 1.15,
                            textShadow: '0 2px 8px rgba(0,0,0,0.4)',
                        }}>
                            {holderName}
                        </h2>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '8px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)', fontWeight: '500' }}>
                                <Mail size={13} color="#FFD97C" />
                                {user?.email || 'Sin email registrado'}
                            </span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: 'rgba(255,255,255,0.72)', fontWeight: '500' }}>
                                <Phone size={13} color="#FFD97C" />
                                {user?.telefono || 'Sin teléfono registrado'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Derecha: Panel Financiero Glass Integrado (4 Métricas con Horas Gratis) */}
                <div
                    className="card-kpi-grid"
                    style={{
                        background: 'rgba(0, 0, 0, 0.32)',
                        backdropFilter: 'blur(16px)',
                        border: '1.5px solid rgba(255, 217, 124, 0.22)',
                        borderRadius: '18px',
                        padding: '14px 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                        flexShrink: 0,
                    }}
                >
                    <div style={{ textAlign: 'center', minWidth: '65px' }}>
                        <p style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Reservas</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '900', color: '#FFD97C', lineHeight: 1.1, margin: '2px 0' }}>{stats.totalCitas}</p>
                        <p style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontWeight: '600' }}>{stats.totalCitasTitular} tit · {stats.totalCitasAsociadas} asoc</p>
                    </div>
                    <div className="card-kpi-divider" style={{ width: '1px', height: '36px', background: 'rgba(255,255,255,0.18)' }} />
                    <div style={{ textAlign: 'center', minWidth: '75px' }}>
                        <p style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Pagado</p>
                        <p style={{ fontSize: '1.25rem', fontWeight: '900', color: '#ffffff', lineHeight: 1.1, margin: '2px 0' }}>Bs {stats.totalPagado.toFixed(0)}</p>
                        <p style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.6)', fontWeight: '600' }}>Acumulado</p>
                    </div>
                    <div className="card-kpi-divider" style={{ width: '1px', height: '36px', background: 'rgba(255,255,255,0.18)' }} />
                    <div style={{ textAlign: 'center', minWidth: '75px' }}>
                        <p style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Horas Gratis</p>
                        <p style={{
                            fontSize: '1.25rem', fontWeight: '900', lineHeight: 1.1, margin: '2px 0',
                            color: horasGratis !== null && horasGratis > 0 ? '#4ade80' : '#94a3b8',
                        }}>
                            {formatGiftBalance(Number(horasGratis ?? 0) * 60)}
                        </p>
                        <p style={{ fontSize: '0.6rem', color: horasGratis !== null && horasGratis > 0 ? '#86efac' : 'rgba(255,255,255,0.5)', fontWeight: '600' }}>
                            {horasGratis !== null && horasGratis > 0 ? 'Disponibles' : 'Sin horas'}
                        </p>
                    </div>
                    <div className="card-kpi-divider" style={{ width: '1px', height: '36px', background: 'rgba(255,255,255,0.18)' }} />
                    <div style={{ textAlign: 'center', minWidth: '75px' }}>
                        <p style={{ fontSize: '0.58rem', fontWeight: '700', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                            {stats.deudaTotal > 0 ? 'Deuda' : 'Estado'}
                        </p>
                        <p style={{
                            fontSize: '1.25rem', fontWeight: '900', lineHeight: 1.1, margin: '2px 0',
                            color: stats.deudaTotal > 0 ? '#ff8a8a' : '#6ee7b7',
                        }}>
                            {stats.deudaTotal > 0 ? `Bs ${stats.deudaTotal.toFixed(0)}` : '✓ Al día'}
                        </p>
                        <p style={{ fontSize: '0.6rem', color: stats.deudaTotal > 0 ? '#ff8a8a' : '#86efac', fontWeight: '600' }}>
                            {stats.deudaTotal > 0 ? 'Pendiente' : 'Sin saldo deudor'}
                        </p>
                    </div>
                </div>
            </div>

            {/* ══ 3. FILA INFERIOR: Red de Miembros Oficial + Franja de Colores de Marca ══ */}
            <div style={{
                position: 'relative', zIndex: 1,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                flexWrap: 'wrap', gap: '16px',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#FFD97C', boxShadow: '0 0 8px #FFD97C' }} />
                    <span style={{ fontSize: '0.68rem', fontWeight: '700', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        CO MARCA NETWORK • MIEMBRO OFICIAL
                    </span>
                </div>

                {/* Franja de colores de marca y sello */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                    <span style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.5)', fontWeight: '800', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                        MEMBRESÍA VIP AUTORIZADA
                    </span>
                    <div style={{ display: 'flex', gap: '5px' }}>
                        {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                            <div key={i} style={{ height: '4px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Controles de Paginación (Siempre visibles con registros) ──
function PaginationControls({ paginaActual, totalPaginas, totalItems, itemsPorPagina, onPaginaChange, isDark }: {
    paginaActual: number;
    totalPaginas: number;
    totalItems: number;
    itemsPorPagina: number;
    onPaginaChange: (p: number) => void;
    isDark: boolean;
}) {
    if (totalItems === 0) return null;
    const paginasSeguras = Math.max(1, totalPaginas);
    const inicio = (paginaActual - 1) * itemsPorPagina + 1;
    const fin = Math.min(paginaActual * itemsPorPagina, totalItems);

    return (
        <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: '22px', padding: '14px 4px 6px', flexWrap: 'wrap', gap: '14px',
            borderTop: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
        }}>
            <p style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b', fontWeight: '600' }}>
                Mostrando <span style={{ color: isDark ? '#ffffff' : '#0f172a', fontWeight: '800' }}>{inicio}–{fin}</span> de {totalItems} {totalItems === 1 ? 'registro' : 'registros'} • Página <span style={{ color: isDark ? '#ffd97c' : colors.primary, fontWeight: '800' }}>{paginaActual}</span> de {paginasSeguras}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <button
                    onClick={() => onPaginaChange(paginaActual - 1)}
                    disabled={paginaActual <= 1}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '7px 14px', borderRadius: '10px',
                        border: isDark ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid #e2e8f0',
                        background: isDark ? 'rgba(22, 10, 43, 0.8)' : '#fff',
                        color: paginaActual <= 1 ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#ffffff' : '#0f172a'),
                        fontSize: '0.78rem', fontWeight: '700',
                        cursor: paginaActual <= 1 ? 'not-allowed' : 'pointer',
                        fontFamily: "'DM Sans', sans-serif",
                        opacity: paginaActual <= 1 ? 0.45 : 1,
                        transition: 'all 0.2s',
                    }}
                >
                    <ChevronLeft size={14} />
                    Anterior
                </button>
                {/* Indicadores de página */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {Array.from({ length: Math.min(paginasSeguras, 5) }, (_, i) => {
                        let pageNum: number;
                        if (paginasSeguras <= 5) {
                            pageNum = i + 1;
                        } else if (paginaActual <= 3) {
                            pageNum = i + 1;
                        } else if (paginaActual >= paginasSeguras - 2) {
                            pageNum = paginasSeguras - 4 + i;
                        } else {
                            pageNum = paginaActual - 2 + i;
                        }
                        return (
                            <button
                                key={pageNum}
                                onClick={() => onPaginaChange(pageNum)}
                                disabled={paginasSeguras <= 1}
                                style={{
                                    width: '32px', height: '32px', borderRadius: '8px',
                                    border: paginaActual === pageNum
                                        ? `1.5px solid ${colors.secondary}60`
                                        : (isDark ? '1.5px solid rgba(255,255,255,0.08)' : '1.5px solid #f1f5f9'),
                                    background: paginaActual === pageNum
                                        ? `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`
                                        : (isDark ? 'rgba(15, 7, 28, 0.6)' : '#f8fafc'),
                                    color: paginaActual === pageNum ? '#ffffff' : (isDark ? '#94a3b8' : '#64748b'),
                                    fontSize: '0.78rem', fontWeight: '800',
                                    cursor: paginasSeguras <= 1 ? 'default' : 'pointer',
                                    fontFamily: "'DM Sans', sans-serif",
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'all 0.2s',
                                }}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={() => onPaginaChange(paginaActual + 1)}
                    disabled={paginaActual >= paginasSeguras}
                    style={{
                        display: 'inline-flex', alignItems: 'center', gap: '5px',
                        padding: '7px 14px', borderRadius: '10px',
                        border: isDark ? '1.5px solid rgba(255,255,255,0.1)' : '1.5px solid #e2e8f0',
                        background: isDark ? 'rgba(22, 10, 43, 0.8)' : '#fff',
                        color: paginaActual >= paginasSeguras ? (isDark ? '#475569' : '#cbd5e1') : (isDark ? '#ffffff' : '#0f172a'),
                        fontSize: '0.78rem', fontWeight: '700',
                        cursor: paginaActual >= paginasSeguras ? 'not-allowed' : 'pointer',
                        fontFamily: "'DM Sans', sans-serif",
                        opacity: paginaActual >= paginasSeguras ? 0.45 : 1,
                        transition: 'all 0.2s',
                    }}
                >
                    Siguiente
                    <ChevronRight size={14} />
                </button>
            </div>
        </div>
    );
}

// ── Badge de estado de cita ──
const estadoBadge = (estado: string) => {
    const map: Record<string, { label: string; color: string; Icon: any }> = {
        'confirmada':     { label: 'Confirmada', color: '#10b981',             Icon: CheckCircle },
        'pendiente':      { label: 'Pendiente',  color: colors.accentDark,     Icon: ClockIcon },
        'cancelada':      { label: 'Cancelada',  color: '#ef4444',             Icon: XCircle },
    };
    const cfg = map[estado.toLowerCase()] ?? { label: estado, color: '#94a3b8', Icon: AlertCircle };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: `${cfg.color}14`, border: `1.5px solid ${cfg.color}30`,
            borderRadius: '100px', padding: '3px 10px',
            fontSize: '0.72rem', fontWeight: '700', color: cfg.color,
        }}>
            <cfg.Icon size={11} strokeWidth={2.5} />
            {cfg.label}
        </span>
    );
};

const pagoBadge = (estado: string) => {
    const map: Record<string, { label: string; color: string; Icon: any }> = {
        'pagado completo': { label: 'Pagado',   color: '#10b981',             Icon: CheckCircle },
        'pagado parcial':  { label: 'Parcial',  color: colors.accentDark,     Icon: AlertCircle },
        'pendiente':       { label: 'Pendiente', color: colors.secondary,     Icon: XCircle },
    };
    const cfg = map[estado.toLowerCase()] ?? { label: estado, color: '#94a3b8', Icon: AlertCircle };
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: `${cfg.color}14`, border: `1.5px solid ${cfg.color}30`,
            borderRadius: '100px', padding: '3px 10px',
            fontSize: '0.72rem', fontWeight: '700', color: cfg.color,
        }}>
            <cfg.Icon size={11} strokeWidth={2.5} />
            {cfg.label}
        </span>
    );
};

const rolBadge = (rol: string) => {
    const isTitular = rol === 'Titular';
    const color = isTitular ? colors.primary : colors.secondary;
    const Icon = isTitular ? Crown : Users;
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '5px',
            background: `${color}14`, border: `1.5px solid ${color}28`,
            borderRadius: '100px', padding: '3px 10px',
            fontSize: '0.72rem', fontWeight: '700', color,
        }}>
            <Icon size={11} strokeWidth={2.5} />
            {rol}
        </span>
    );
};

// ── Cita Row Card (usa el patrón de ProjectCard: image-side + content-side) ──
function CitaRow({ cita, plan, rol, index, onVerPagos, onVerSesiones }: {
    cita: Cita;
    plan: PrecioPlanServicio | undefined;
    rol: string;
    index: number;
    onVerPagos: () => void;
    onVerSesiones: () => void;
}) {
    const { isDark } = useTheme();
    const [hovered, setHovered] = useState(false);
    const accentColor = rol === 'Titular' ? colors.primary : colors.secondary;
    const isEvento = cita.reglas_plan_reserva?.is_event;
    const nombreServicio = isEvento ? `Evento: ${cita.reglas_plan_reserva?.evento_titulo}` : plan ? `${plan.servicio_nombre} — ${plan.plan_nombre}` : 'Reserva sin servicio';

    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                background: isDark ? 'rgba(22, 10, 43, 0.85)' : '#fff',
                backdropFilter: 'blur(10px)',
                borderRadius: '18px',
                border: hovered 
                    ? `1.5px solid ${accentColor}60` 
                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.20)' : '1.5px solid #f1f5f9'),
                boxShadow: hovered
                    ? (isDark ? `0 14px 40px rgba(0,0,0,0.6), 0 0 20px ${accentColor}25` : `0 14px 40px ${accentColor}14, 0 3px 16px rgba(0,0,0,0.05)`)
                    : (isDark ? '0 2px 12px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.04)'),
                transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
                transition: 'transform 0.3s cubic-bezier(.16,1,.3,1), box-shadow 0.3s, border-color 0.3s',
                animation: `rowFadeIn 0.35s cubic-bezier(.16,1,.3,1) ${Math.min(index, 7) * 0.04}s both`,
            }}
        >

            <div style={{ padding: '16px 20px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}>

                    {/* Left: badges + service name */}
                    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '7px', marginBottom: '8px' }}>
                            {rolBadge(rol)}
                            <span style={{
                                fontSize: '0.72rem', fontWeight: '700', color: isDark ? '#cbd5e1' : '#94a3b8',
                                background: isDark ? '#140827' : '#f8fafc', border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #f1f5f9',
                                borderRadius: '6px', padding: '2px 8px', fontFamily: 'monospace',
                            }}>
                                {cita.codigo}
                            </span>
                        </div>
                        <p style={{
                            fontSize: '0.9rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            letterSpacing: '-0.01em',
                        }}>
                            {nombreServicio}
                        </p>
                    </div>

                    {/* Mid: fecha + hora */}
                    <div style={{ flex: '0 0 auto', textAlign: 'center' }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '7px',
                            background: isDark ? `${accentColor}18` : `${accentColor}08`, border: isDark ? `1.5px solid ${accentColor}30` : `1.5px solid ${accentColor}18`,
                            borderRadius: '10px', padding: '7px 14px',
                        }}>
                            <Calendar size={14} color={accentColor} strokeWidth={2} />
                            <div>
                                <p style={{ fontSize: '0.78rem', fontWeight: '700', color: isDark ? '#f1f5f9' : '#374151', lineHeight: 1.2 }}>
                                    {new Date(cita.fecha_inicio!).toLocaleDateString('es-BO', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' })}
                                </p>
                                <p style={{ fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '2px' }}>
                                    {cita.hora_inicio || 'Horario por definir'}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Mid: estado + pago */}
                    <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'flex-start' }}>
                        {estadoBadge(cita.estado)}
                        {!isReservationCancelled(cita) && pagoBadge(cita.estado_pago)}
                    </div>

                    {/* Mid: montos */}
                    <div style={{ flex: '0 0 auto' }}>
                        <p style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.02em', lineHeight: 1 }}>
                            Bs {Number(cita.total).toFixed(2)}
                        </p>
                        <p style={{ fontSize: '0.7rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '3px' }}>
                            Pagado: <span style={{ color: colors.secondaryLight, fontWeight: '700' }}>Bs {Number(cita.monto_pagado || 0).toFixed(2)}</span>
                        </p>
                    </div>

                    {/* Right: acciones responsivas */}
                    {!isReservationCancelled(cita) && <div style={{ flex: '1 1 auto', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button
                            className="cita-action-btn"
                            onClick={onVerSesiones}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '8px 14px', borderRadius: '10px',
                                background: isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(97, 37, 123, 0.05)',
                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.35)' : '1.5px solid rgba(97, 37, 123, 0.22)',
                                fontSize: '0.78rem', fontWeight: '700',
                                color: isDark ? '#ffd97c' : colors.primary,
                                cursor: 'pointer', transition: 'all 0.2s',
                                fontFamily: "'DM Sans', sans-serif",
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.borderColor = colors.secondary;
                                e.currentTarget.style.background = isDark ? 'rgba(193, 52, 139, 0.25)' : 'rgba(97, 37, 123, 0.1)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.borderColor = isDark ? 'rgba(193, 52, 139, 0.35)' : 'rgba(97, 37, 123, 0.22)';
                                e.currentTarget.style.background = isDark ? 'rgba(97, 37, 123, 0.15)' : 'rgba(97, 37, 123, 0.05)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <ExternalLink size={13} />
                            Sesiones
                        </button>
                        <button
                            className="cita-action-btn"
                            onClick={onVerPagos}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                padding: '8px 16px', borderRadius: '10px',
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                border: 'none',
                                fontSize: '0.78rem', fontWeight: '700', color: '#fff',
                                cursor: 'pointer', transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                                fontFamily: "'DM Sans', sans-serif",
                                boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-1px)';
                                e.currentTarget.style.boxShadow = '0 6px 18px rgba(193, 52, 139, 0.45)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(97, 37, 123, 0.35)';
                            }}
                        >
                            <List size={13} />
                            Pagos
                        </button>
                    </div>}
                </div>

                {/* Titular info para asociados */}
                {rol === 'Asociado' && cita.cliente_info && (
                    <div style={{
                        marginTop: '12px', paddingTop: '10px',
                        borderTop: isDark ? '1px solid rgba(193, 52, 139, 0.15)' : '1px solid #f1f5f9',
                        display: 'flex', alignItems: 'center', gap: '8px',
                    }}>
                        <div style={{ width: '22px', height: '22px', borderRadius: '6px', background: `${colors.primary}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Crown size={11} color={colors.primary} />
                        </div>
                        <span style={{ fontSize: '0.74rem', color: isDark ? '#cbd5e1' : '#64748b' }}>
                            Titular: <strong style={{ color: isDark ? '#ffffff' : '#0f172a' }}>{cita.cliente_info.first_name} {cita.cliente_info.last_name}</strong>
                            {cita.cliente_info.telefono && ` · ${cita.cliente_info.telefono}`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Helpers de Formateo de Agenda ──
const formatFechaLegible = (fechaStr?: string | null) => {
    if (!fechaStr) return 'Fecha por coordinar';
    try {
        const [y, m, d] = fechaStr.split('-').map(Number);
        if (!y || !m || !d) return fechaStr;
        const dateObj = new Date(y, m - 1, d);
        const formatted = dateObj.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    } catch {
        return fechaStr;
    }
};

const getDiasRestantes = (fechaStr?: string | null) => {
    if (!fechaStr) return null;
    try {
        const [y, m, d] = fechaStr.split('-').map(Number);
        if (!y || !m || !d) return null;
        const citaDate = new Date(y, m - 1, d);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffMs = citaDate.getTime() - today.getTime();
        return Math.round(diffMs / (1000 * 60 * 60 * 24));
    } catch {
        return null;
    }
};

const formatHoraCorta = (hora?: string | null) => {
    if (!hora) return '';
    return hora.slice(0, 5);
};



// ── Section wrapper con animación CSS pura (no IntersectionObserver) ──
function Section({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
    return (
        <div style={{
            animation: `sectionFadeUp 0.65s cubic-bezier(.16,1,.3,1) ${delay}s both`,
        }}>
            {children}
        </div>
    );
}

// ── Section header (misma tipografía que AboutusProjects) ──
function SectionTitle({ label, title, accent, sub }: { label: string; title: string; accent: string; sub?: string }) {
    const { isDark } = useTheme();
    return (
        <div style={{ marginBottom: '28px' }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '7px',
                background: `${accent}12`, border: `1.5px solid ${accent}25`,
                borderRadius: '100px', padding: '5px 14px',
                fontSize: '0.7rem', fontWeight: '800', color: accent,
                letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px',
            }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: accent, display: 'inline-block' }} />
                {label}
            </span>
            <h2 style={{
                fontSize: 'clamp(1.2rem, 2.5vw, 1.7rem)', fontWeight: '900',
                color: isDark ? '#ffffff' : '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.1,
            }}>
                {title}
            </h2>
            {sub && <p style={{ fontSize: '0.88rem', color: isDark ? '#cbd5e1' : '#94a3b8', fontWeight: '500', marginTop: '6px' }}>{sub}</p>}
        </div>
    );
}


// ── Correspondencia Item (componente separado para evitar hooks en callbacks) ──
const CorrespondenciaItem: React.FC<{
    cita: Cita;
    plan?: PrecioPlanServicio;
    rol: string;
    onVerHistorial: () => void;
}> = ({ cita, plan, rol, onVerHistorial }) => {
    const { isDark } = useTheme();
    const [hov, setHov] = useState(false);
    return (
        <div
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                background: isDark ? 'rgba(22, 10, 43, 0.85)' : '#fff', borderRadius: '16px',
                border: hov 
                    ? `1.5px solid ${colors.secondary}60` 
                    : (isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9'),
                boxShadow: hov 
                    ? (isDark ? '0 10px 32px rgba(0,0,0,0.6)' : `0 10px 32px ${colors.secondary}12`) 
                    : (isDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(0,0,0,0.04)'),
                transition: 'all 0.3s cubic-bezier(.16,1,.3,1)',
                transform: hov ? 'translateY(-2px)' : 'translateY(0)',
            }}
        >
            <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', alignItems: 'center', marginBottom: '7px' }}>
                        {rolBadge(rol)}
                        <span style={{ fontSize: '0.72rem', fontWeight: '700', color: isDark ? '#cbd5e1' : '#94a3b8', background: isDark ? '#140827' : '#f8fafc', border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #f1f5f9', borderRadius: '6px', padding: '2px 8px', fontFamily: 'monospace' }}>{cita.codigo}</span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: `${colors.secondaryLight}10`, border: `1.5px solid ${colors.secondaryLight}25`, borderRadius: '100px', padding: '3px 10px', fontSize: '0.7rem', fontWeight: '700', color: colors.secondary }}>
                            <MessageSquare size={11} />Correspondencia
                        </span>
                    </div>
                    <p style={{ fontSize: '0.88rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {plan ? `${plan.servicio_nombre} — ${plan.plan_nombre}` : 'Desconocido'}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {estadoBadge(cita.estado)}
                    <button
                        onClick={onVerHistorial}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            padding: '8px 16px', borderRadius: '10px',
                            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                            border: 'none', fontSize: '0.78rem', fontWeight: '700', color: '#fff',
                            cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                            boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                            transition: 'all 0.2s cubic-bezier(.16,1,.3,1)',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(193, 52, 139, 0.45)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 14px rgba(97, 37, 123, 0.35)';
                        }}
                    >
                        <History size={13} /> Ver Historial
                    </button>
                </div>
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════
const MiDashboard: React.FC = () => {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [citas, setCitas] = useState<Cita[]>([]);
    const [planes, setPlanes] = useState<PrecioPlanServicio[]>([]);
    const [tabActiva, setTabActiva] = useState<'todas' | 'titular' | 'asociadas'>('todas');
    const [citaSeleccionada, setCitaSeleccionada] = useState<Cita | null>(null);
    const [modalListadoPagosOpen, setModalListadoPagosOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [tabPrincipalActiva, setTabPrincipalActiva] = useState<'reservas' | 'correspondencia'>('reservas');
    const [citasConCorrespondencia, setCitasConCorrespondencia] = useState<Cita[]>([]);
    const [modalHistorialCorrespondenciaOpen, setModalHistorialCorrespondenciaOpen] = useState(false);
    const [citaSeleccionadaCorrespondencia, setCitaSeleccionadaCorrespondencia] = useState<Cita | null>(null);
    const [searchFocused, setSearchFocused] = useState(false);
    const [paginaActual, setPaginaActual] = useState(1);
    const [paginaCorr, setPaginaCorr] = useState(1);
    const [horasGratis, setHorasGratis] = useState<number | null>(null);
    const [modalWifiOpen, setModalWifiOpen] = useState(false);
    const [wifiCopiado, setWifiCopiado] = useState(false);
    const [calendarDate, setCalendarDate] = useState(() => new Date());
    const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);

    const [stats, setStats] = useState({
        totalCitas: 0, totalPagado: 0, servicioMasUsado: 'Sin datos',
        usoPorServicio: {} as Record<string, { count: number; total: number }>,
        deudaTotal: 0, totalCitasValor: 0,
        totalCitasTitular: 0, totalCitasAsociadas: 0,
        totalPagadoTitular: 0, totalPagadoAsociadas: 0,
    });

    const determinarRolCita = useCallback((cita: Cita): string => {
        if (!user) return 'Desconocido';
        if (cita.cliente === user.id) return 'Titular';
        if (Array.isArray(cita.clientes_asociados) && cita.clientes_asociados.includes(user.id)) return 'Asociado';
        if (Array.isArray(cita.clientes_asociados_info) && cita.clientes_asociados_info.some((a: any) => a.id === user.id)) return 'Asociado';
        return 'Desconocido';
    }, [user]);

    const handleCitaEvent = useCallback((action: string, citaData: any) => {
    // Solo procesar citas donde el usuario es titular o asociado
    const esTitular = citaData.cliente === user?.id;
    const esAsociado = Array.isArray(citaData.clientes_asociados)
        && citaData.clientes_asociados.includes(user?.id);

    if (!esTitular && !esAsociado) return;

    if (action === 'create') {
        setCitas(prev => {
            const exists = prev.some(c => c.id === citaData.id);
            if (exists) return prev;
            return [citaData, ...prev];
        });
        // Actualizar stats incrementalmente
        setStats(prev => ({
            ...prev,
            totalCitas: prev.totalCitas + 1,
            totalCitasValor: prev.totalCitasValor + Number(citaData.total || 0),
            totalCitasTitular: esTitular ? prev.totalCitasTitular + 1 : prev.totalCitasTitular,
            totalCitasAsociadas: esAsociado ? prev.totalCitasAsociadas + 1 : prev.totalCitasAsociadas,
        }));
    } else if (action === 'update') {
        setCitas(prev => prev.map(c => c.id === citaData.id ? citaData : c));
    } else if (action === 'delete') {
        setCitas(prev => prev.filter(c => c.id !== citaData.id));
        setStats(prev => ({
            ...prev,
            totalCitas: Math.max(0, prev.totalCitas - 1),
            totalCitasTitular: esTitular ? Math.max(0, prev.totalCitasTitular - 1) : prev.totalCitasTitular,
            totalCitasAsociadas: esAsociado ? Math.max(0, prev.totalCitasAsociadas - 1) : prev.totalCitasAsociadas,
        }));
    }
}, [user?.id]);

useWebSocketCitas(handleCitaEvent);

    useEffect(() => {
        const cargarDatos = async () => {
            setIsLoading(true);
            try {
                if (user?.id) {
                    const [todasLasCitas, listaPlanes] = await Promise.all([
                        fetchCitasPorUsuarioCompleto(user.id),
                        fetchPrecioPlanesServicios()
                    ]);
                    setCitas(todasLasCitas);
                    setPlanes(listaPlanes);

                    const conCorr: Cita[] = [];
                    for (const cita of todasLasCitas) {
                        const cfg = await fetchCitaCorrespondenciaPorCita(cita.id);
                        if (cfg?.habilitado) conCorr.push(cita);
                    }
                    setCitasConCorrespondencia(conCorr);

                    // ── Cargar horas gratuitas de afiliado / cliente ──
                    try {
                        const af = await fetchMiAfiliacion();
                        if (af && Number(af.horas_gratuitas_disponibles ?? 0) > 0) {
                            setHorasGratis(Number(af.horas_gratuitas_disponibles));
                        } else {
                            const horasRegalo = await fetchUsoHorasRegaloPorCliente(user.id);
                            if (horasRegalo && horasRegalo.length > 0) {
                                const disponibles = horasRegalo.reduce((acc, u) => acc + (u.horas_regalo_aplicadas - (u.horas_usadas || 0)), 0);
                                setHorasGratis(Math.max(0, disponibles));
                            } else {
                                setHorasGratis(0);
                            }
                        }
                    } catch {
                        setHorasGratis(0);
                    }
                }
            } catch (e) { console.error(e); }
            finally { setIsLoading(false); }
        };
        cargarDatos();
    }, [user, determinarRolCita]);

    const citasFiltradas = useMemo(() => {
        if (!user) return [];
        let f = citas.filter(c => {
            const rol = determinarRolCita(c);
            if (tabActiva === 'titular') return rol === 'Titular';
            if (tabActiva === 'asociadas') return rol === 'Asociado';
            return true;
        });
        if (busqueda.trim()) {
            const s = busqueda.toLowerCase();
            f = f.filter(c => {
                const p = planes.find(p => p.id === c.precio_plan_servicio);
                const n = p ? `${p.servicio_nombre} ${p.plan_nombre}`.toLowerCase() : '';
                return c.codigo.toLowerCase().includes(s) || n.includes(s) || c.estado.toLowerCase().includes(s) || c.estado_pago.toLowerCase().includes(s);
            });
        }
        return f.sort((a, b) => b.id - a.id);
    }, [citas, tabActiva, user, busqueda, planes, determinarRolCita]);

    useEffect(() => {
        const conteo: Record<string, { count: number; total: number }> = {};
        let totalPagado = 0, deudaTotal = 0, totalCitasValor = 0;
        let titCnt = 0, asCnt = 0, titPag = 0, asPag = 0;

        citas.filter(c => !isReservationCancelled(c)).forEach(c => {
            const p = planes.find(p => p.id === c.precio_plan_servicio);
            const n = p ? `${p.servicio_nombre} - ${p.plan_nombre}` : 'Desconocido';
            if (!conteo[n]) conteo[n] = { count: 0, total: 0 };
            conteo[n].count++;
            conteo[n].total += Number(c.total);
            totalCitasValor += Number(c.total);
            totalPagado += Number(c.monto_pagado) || 0;
            deudaTotal += Math.max(0, Number(c.total) - (Number(c.monto_pagado) || 0));
            const rol = determinarRolCita(c);
            if (rol === 'Titular') { titCnt++; titPag += Number(c.monto_pagado) || 0; }
            else if (rol === 'Asociado') { asCnt++; asPag += Number(c.monto_pagado) || 0; }
        });

        setStats({
            totalCitas: citas.filter(c => !isReservationCancelled(c)).length,
            totalPagado,
            servicioMasUsado: Object.entries(conteo).sort((a, b) => b[1].count - a[1].count)[0]?.[0] ?? 'Sin datos',
            usoPorServicio: conteo,
            deudaTotal, totalCitasValor,
            totalCitasTitular: titCnt, totalCitasAsociadas: asCnt,
            totalPagadoTitular: titPag, totalPagadoAsociadas: asPag,
        });
    }, [citas, planes, determinarRolCita]);

    // ── Paginación de reservas ──
    const totalPaginasReservas = Math.ceil(citasFiltradas.length / ITEMS_POR_PAGINA);
    const citasPaginadas = useMemo(() => {
        const inicio = (paginaActual - 1) * ITEMS_POR_PAGINA;
        return citasFiltradas.slice(inicio, inicio + ITEMS_POR_PAGINA);
    }, [citasFiltradas, paginaActual]);

    // ── Paginación de correspondencia ──
    const totalPaginasCorr = Math.ceil(citasConCorrespondencia.length / ITEMS_POR_PAGINA);
    const corrPaginada = useMemo(() => {
        const inicio = (paginaCorr - 1) * ITEMS_POR_PAGINA;
        return citasConCorrespondencia.slice(inicio, inicio + ITEMS_POR_PAGINA);
    }, [citasConCorrespondencia, paginaCorr]);

    // Reset de página al cambiar filtros
    useEffect(() => { setPaginaActual(1); }, [tabActiva, busqueda]);
    useEffect(() => { setPaginaCorr(1); }, [busqueda]);

    const proximaCita = useMemo(() => {
        if (!citas || citas.length === 0) return null;
        const todayStr = new Date().toISOString().split('T')[0];

        // 1. Citas activas (no canceladas ni finalizadas)
        const activas = citas.filter(c => {
            const est = (c.estado || '').toLowerCase();
            const ejec = c.estado_ejecucion;
            return est !== 'cancelada' && ejec !== 'Cancelada' && ejec !== 'Finalizada';
        });

        if (activas.length === 0) {
            // Si no hay activas en curso o futuras, mostrar la más reciente no cancelada (o la última)
            const noCanceladas = citas.filter(c => !isReservationCancelled(c));
            return noCanceladas.length > 0 ? noCanceladas[0] : null;
        }

        // 2. Priorizar citas de hoy o en adelante
        const futuras = activas.filter(c => c.fecha_inicio && c.fecha_inicio >= todayStr);
        if (futuras.length > 0) {
            // Ordenar de la más próxima a la más lejana
            return [...futuras].sort((a, b) => {
                const dateComp = (a.fecha_inicio || '').localeCompare(b.fecha_inicio || '');
                if (dateComp !== 0) return dateComp;
                return (a.hora_inicio || '').localeCompare(b.hora_inicio || '');
            })[0];
        }

        // 3. Si todas las activas tienen fecha anterior, mostrar la más reciente por id
        return [...activas].sort((a, b) => b.id - a.id)[0];
    }, [citas]);

    const planProxima = useMemo(() => {
        if (!proximaCita) return null;
        return planes.find(p => p.id === proximaCita.precio_plan_servicio);
    }, [proximaCita, planes]);

    const rolProxima = useMemo(() => {
        if (!proximaCita) return '';
        return determinarRolCita(proximaCita);
    }, [proximaCita, determinarRolCita]);

    const diasRestantes = useMemo(() => {
        if (!proximaCita?.fecha_inicio) return null;
        return getDiasRestantes(proximaCita.fecha_inicio);
    }, [proximaCita]);

    const tieneCorrespondenciaProxima = useMemo(() => {
        if (!proximaCita) return false;
        return citasConCorrespondencia.some(c => c.id === proximaCita.id);
    }, [proximaCita, citasConCorrespondencia]);

    const isProximaEvento = proximaCita?.reglas_plan_reserva?.is_event;
    const nombreServicioProxima = isProximaEvento ? 'Evento de Comunidad' : (proximaCita?.servicio_info?.nombre || planProxima?.servicio_nombre || 'Servicio Co Marca');
    const nombrePlanProxima = isProximaEvento ? proximaCita?.reglas_plan_reserva?.evento_titulo : (proximaCita?.plan_info?.plan_nombre || planProxima?.plan_nombre || 'Plan de Espacio');
    const nombreEspacioProxima = isProximaEvento ? 'Área de Eventos' : (proximaCita?.espacio_info?.nombre || 'Espacio asignado');
    const nombreSucursalProxima = proximaCita?.sucursal_info?.nombre || 'Co Marca Coworking';

    // ── Lógica del Calendario Interactivo Mensual ──
    const citasPorFecha = useMemo(() => {
        const map: Record<string, Cita[]> = {};
        citas.forEach(c => {
            if (c.fecha_inicio) {
                const dateKey = c.fecha_inicio.substring(0, 10);
                if (!map[dateKey]) map[dateKey] = [];
                map[dateKey].push(c);
            }
        });
        return map;
    }, [citas]);

    const todayStr = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    const calendarGrid = useMemo(() => {
        const y = calendarDate.getFullYear();
        const m = calendarDate.getMonth();
        const firstDay = new Date(y, m, 1).getDay();
        const startOffset = (firstDay + 6) % 7; // Lunes = 0
        const totalDays = new Date(y, m + 1, 0).getDate();
        const prevMonthTotalDays = new Date(y, m, 0).getDate();
        const cells: Array<{
            dayNum: number;
            dateStr: string;
            isCurrentMonth: boolean;
            isToday: boolean;
            citas: Cita[];
        }> = [];

        // Días previos
        for (let i = startOffset - 1; i >= 0; i--) {
            const dayNum = prevMonthTotalDays - i;
            const prevMonthDate = new Date(y, m - 1, dayNum);
            const pY = prevMonthDate.getFullYear();
            const pM = String(prevMonthDate.getMonth() + 1).padStart(2, '0');
            const pD = String(dayNum).padStart(2, '0');
            const dateStr = `${pY}-${pM}-${pD}`;
            cells.push({
                dayNum,
                dateStr,
                isCurrentMonth: false,
                isToday: dateStr === todayStr,
                citas: citasPorFecha[dateStr] || [],
            });
        }

        // Días mes actual
        for (let d = 1; d <= totalDays; d++) {
            const pY = y;
            const pM = String(m + 1).padStart(2, '0');
            const pD = String(d).padStart(2, '0');
            const dateStr = `${pY}-${pM}-${pD}`;
            cells.push({
                dayNum: d,
                dateStr,
                isCurrentMonth: true,
                isToday: dateStr === todayStr,
                citas: citasPorFecha[dateStr] || [],
            });
        }

        // Días siguientes para completar semanas (múltiplo de 7)
        const remaining = (7 - (cells.length % 7)) % 7;
        for (let d = 1; d <= remaining; d++) {
            const nextMonthDate = new Date(y, m + 1, d);
            const nY = nextMonthDate.getFullYear();
            const nM = String(nextMonthDate.getMonth() + 1).padStart(2, '0');
            const nD = String(d).padStart(2, '0');
            const dateStr = `${nY}-${nM}-${nD}`;
            cells.push({
                dayNum: d,
                dateStr,
                isCurrentMonth: false,
                isToday: dateStr === todayStr,
                citas: citasPorFecha[dateStr] || [],
            });
        }

        return cells;
    }, [calendarDate, citasPorFecha, todayStr]);

    const handlePrevMonth = () => {
        setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCalendarDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    };

    const handleGoToday = () => {
        const now = new Date();
        setCalendarDate(new Date(now.getFullYear(), now.getMonth(), 1));
        setSelectedCalendarDate(todayStr);
    };

    const totalDiasConReservaMes = useMemo(() => {
        return calendarGrid.filter(c => c.isCurrentMonth && c.citas.length > 0).length;
    }, [calendarGrid]);

    const citasFechaSeleccionada = useMemo(() => {
        if (!selectedCalendarDate) return [];
        return citasPorFecha[selectedCalendarDate] || [];
    }, [selectedCalendarDate, citasPorFecha]);

    if (isLoading) return <Loading text="Cargando tu dashboard..." />;

    const initials = user ? `${user.first_name?.[0] ?? ''}${user.last_name?.[0] ?? ''}`.toUpperCase() : 'U';
    const profession = getProfessionColor(user);
    const ringTier = getUserRingTier(user);
    const tierConfig = TIER_CONFIGS[ringTier] || TIER_CONFIGS.comun;

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: 'transparent', minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
                * { box-sizing: border-box; }
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes blobFloat {
                    0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
                    50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
                }
                @keyframes sectionFadeUp {
                    from { opacity: 0; transform: translateY(24px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes rowFadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes pulseD {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(193, 52, 139, 0.4); }
                    50%       { box-shadow: 0 0 0 6px rgba(193, 52, 139, 0); }
                }
                .blob-dash { animation: blobFloat 9s ease-in-out infinite; }
                .tab-btn { transition: all 0.2s cubic-bezier(.16,1,.3,1); }
                .tab-btn:hover { transform: translateY(-1px); }

                @media (max-width: 768px) {
                    .card-kpi-grid {
                        display: grid !important;
                        grid-template-columns: repeat(2, 1fr) !important;
                        gap: 12px !important;
                        width: 100% !important;
                    }
                    .card-kpi-divider {
                        display: none !important;
                    }
                    .cita-action-btn {
                        flex: 1 1 120px !important;
                        justify-content: center !important;
                    }
                    .dashboard-search-container {
                        max-width: 100% !important;
                    }
                }
                @media (max-width: 480px) {
                    .card-kpi-grid {
                        grid-template-columns: 1fr 1fr !important;
                    }
                }
            `}</style>

            <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(16px, 3vw, 40px) clamp(12px, 3vw, 28px)' }}>

                {/* ══ TARJETA DIGITAL CO MARCA (HERO AMPLIO & ALTO) ══ */}
                <Section>
                    <div style={{ marginBottom: '32px' }}>
                        <CoMarcaCard
                            user={user}
                            stats={stats}
                            tierConfig={tierConfig}
                            profession={profession}
                            initials={initials}
                            isDark={isDark}
                            horasGratis={horasGratis}
                        />
                    </div>
                </Section>

                {/* ══ AGENDA & CALENDARIO INTERACTIVO (ANCHO COMPLETO) ══ */}
                <Section delay={0.1}>
                    <div style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.85)' : '#ffffff',
                        borderRadius: '24px',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9',
                        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.35)' : '0 2px 20px rgba(0,0,0,0.04)',
                        backdropFilter: 'blur(12px)',
                        padding: 'clamp(20px, 3.2vw, 36px)',
                        marginBottom: '32px',
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 420px), 1fr))',
                            gap: 'clamp(24px, 3.5vw, 40px)',
                            alignItems: 'stretch',
                        }}>
                            {/* ── COLUMNA 1: TU PRÓXIMA RESERVA ── */}
                            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                {/* Header con icono */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <SectionTitle
                                        label="Agenda de Reservas"
                                        title="Tu Próxima Reserva"
                                        accent={colors.primary}
                                        sub={proximaCita ? "Reserva programada y confirmada" : "Sin reservas pendientes"}
                                    />
                                    <div style={{
                                        width: '42px', height: '42px', borderRadius: '13px',
                                        background: `${colors.primary}12`, border: `1.5px solid ${colors.primary}25`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: isDark ? '#ffd97c' : colors.primary
                                    }}>
                                        <CalendarCheck size={20} />
                                    </div>
                                </div>

                                {proximaCita ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between', gap: '18px' }}>
                                        {/* Cabecera del servicio */}
                                        <div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
                                                {rolProxima && rolBadge(rolProxima)}
                                                <span style={{
                                                    fontSize: '0.72rem', fontWeight: '700',
                                                    color: isDark ? '#cbd5e1' : '#64748b',
                                                    background: isDark ? '#140827' : '#f8fafc',
                                                    border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #e2e8f0',
                                                    borderRadius: '6px', padding: '2px 8px', fontFamily: 'monospace'
                                                }}>
                                                    {proximaCita.codigo}
                                                </span>
                                                {estadoBadge(proximaCita.estado)}
                                                {pagoBadge(proximaCita.estado_pago)}
                                            </div>

                                            <h3 style={{
                                                fontSize: '1.25rem', fontWeight: '900',
                                                color: isDark ? '#ffffff' : '#0f172a',
                                                letterSpacing: '-0.02em', marginBottom: '4px'
                                            }}>
                                                {nombreServicioProxima}
                                            </h3>
                                            <p style={{
                                                fontSize: '0.86rem', fontWeight: '700',
                                                color: isDark ? '#ffd97c' : colors.primary,
                                            }}>
                                                {nombrePlanProxima}
                                            </p>
                                        </div>

                                        {/* Caja de Datos de Agenda */}
                                        <div style={{
                                            background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(97, 37, 123, 0.03)',
                                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.2)' : '1.5px solid rgba(97, 37, 123, 0.08)',
                                            borderRadius: '16px', padding: '16px 18px',
                                            display: 'flex', flexDirection: 'column', gap: '12px'
                                        }}>
                                            {/* Fecha */}
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{
                                                        width: '32px', height: '32px', borderRadius: '8px',
                                                        background: `${colors.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        color: isDark ? '#ffd97c' : colors.primary
                                                    }}>
                                                        <Calendar size={15} />
                                                    </div>
                                                    <div>
                                                        <p style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fecha de Reserva</p>
                                                        <p style={{ fontSize: '0.88rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>
                                                            {formatFechaLegible(proximaCita.fecha_inicio)}
                                                        </p>
                                                    </div>
                                                </div>
                                                {/* Pill de cuenta regresiva */}
                                                {diasRestantes !== null && (
                                                    <span style={{
                                                        fontSize: '0.72rem', fontWeight: '800',
                                                        padding: '4px 10px', borderRadius: '100px',
                                                        background: diasRestantes === 0
                                                            ? (isDark ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5')
                                                            : diasRestantes === 1
                                                            ? (isDark ? 'rgba(255, 217, 124, 0.2)' : '#fef3c7')
                                                            : diasRestantes > 1
                                                            ? (isDark ? 'rgba(193, 52, 139, 0.2)' : '#fce7f3')
                                                            : (isDark ? 'rgba(148, 163, 184, 0.15)' : '#f1f5f9'),
                                                        color: diasRestantes === 0
                                                            ? (isDark ? '#34d399' : '#065f46')
                                                            : diasRestantes === 1
                                                            ? (isDark ? '#ffd97c' : '#b45309')
                                                            : diasRestantes > 1
                                                            ? (isDark ? '#f472b6' : colors.secondary)
                                                            : '#94a3b8',
                                                        border: diasRestantes === 0
                                                            ? '1.5px solid rgba(16, 185, 129, 0.3)'
                                                            : diasRestantes === 1
                                                            ? '1.5px solid rgba(255, 217, 124, 0.4)'
                                                            : diasRestantes > 1
                                                            ? '1.5px solid rgba(193, 52, 139, 0.25)'
                                                            : '1.5px solid rgba(148, 163, 184, 0.2)',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {diasRestantes === 0 ? '🟢 ¡Es hoy!' : diasRestantes === 1 ? '⚡ Mañana' : diasRestantes > 1 ? `En ${diasRestantes} días` : 'Completada'}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Horario */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '32px', height: '32px', borderRadius: '8px',
                                                    background: `${colors.secondary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: colors.secondary
                                                }}>
                                                    <ClockIcon size={15} />
                                                </div>
                                                <div>
                                                    <p style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Horario Programado</p>
                                                    <p style={{ fontSize: '0.88rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a' }}>
                                                        {proximaCita.hora_inicio ? `${formatHoraCorta(proximaCita.hora_inicio)} a ${formatHoraCorta(proximaCita.hora_fin)}` : 'Horario flexible'}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Ubicación y Espacio */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '32px', height: '32px', borderRadius: '8px',
                                                    background: 'rgba(234, 132, 6, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: colors.accentDark
                                                }}>
                                                    <MapPin size={15} />
                                                </div>
                                                <div>
                                                    <p style={{ fontSize: '0.7rem', color: isDark ? '#94a3b8' : '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Ubicación & Espacio</p>
                                                    <p style={{ fontSize: '0.85rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a' }}>
                                                        {nombreSucursalProxima} <span style={{ color: isDark ? '#ffd97c' : colors.primary }}>·</span> {nombreEspacioProxima}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Botones de Acción */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '4px' }}>
                                            <button
                                                onClick={() => navigate(`/mis-citas/${proximaCita.id}/sesiones`)}
                                                style={{
                                                    flex: '1 1 140px',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                                                    padding: '10px 18px', borderRadius: '12px',
                                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                    border: 'none', color: '#fff',
                                                    fontSize: '0.82rem', fontWeight: '800',
                                                    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                    boxShadow: '0 4px 14px rgba(97, 37, 123, 0.35)',
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(193, 52, 139, 0.45)';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(97, 37, 123, 0.35)';
                                                }}
                                            >
                                                <CalendarCheck size={15} />
                                                Ver Sesiones
                                            </button>

                                            <button
                                                onClick={() => navigate(`/mis-citas/${proximaCita.id}/detalles`)}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                                    padding: '10px 16px', borderRadius: '12px',
                                                    background: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
                                                    border: isDark ? '1.5px solid rgba(193, 52, 139, 0.3)' : '1.5px solid #e2e8f0',
                                                    color: isDark ? '#ffffff' : '#334155',
                                                    fontSize: '0.82rem', fontWeight: '700',
                                                    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                    transition: 'all 0.2s ease',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.borderColor = colors.secondary;
                                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.borderColor = isDark ? 'rgba(193, 52, 139, 0.3)' : '#e2e8f0';
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                }}
                                            >
                                                <ExternalLink size={14} />
                                                Detalles
                                            </button>

                                            {tieneCorrespondenciaProxima && (
                                                <button
                                                    onClick={() => {
                                                        setCitaSeleccionadaCorrespondencia(proximaCita);
                                                        setModalHistorialCorrespondenciaOpen(true);
                                                    }}
                                                    style={{
                                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                                        padding: '10px 14px', borderRadius: '12px',
                                                        background: `${colors.accent}18`,
                                                        border: `1.5px solid ${colors.accentDark}35`,
                                                        color: isDark ? '#ffd97c' : colors.accentDark,
                                                        fontSize: '0.82rem', fontWeight: '800',
                                                        cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                    title="Ver valija de correspondencia"
                                                >
                                                    <Mail size={14} />
                                                    Valija
                                                </button>
                                            )}
                                        </div>

                                        {/* Micro banner de membresía en el pie */}
                                        <div style={{
                                            marginTop: '6px',
                                            background: isDark ? 'rgba(255, 217, 124, 0.04)' : 'rgba(97, 37, 123, 0.03)',
                                            border: isDark ? '1px solid rgba(255, 217, 124, 0.14)' : '1px solid rgba(97, 37, 123, 0.08)',
                                            borderRadius: '12px', padding: '10px 14px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <Shield size={14} color={tierConfig.badgeColor} />
                                                <span style={{ fontSize: '0.74rem', fontWeight: '700', color: isDark ? '#e2e8f0' : '#334155' }}>
                                                    Membresía <span style={{ color: tierConfig.badgeColor }}>{tierConfig.label}</span>
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => navigate(ROUTES.perfil)}
                                                style={{
                                                    background: 'transparent', border: 'none',
                                                    color: isDark ? '#ffd97c' : colors.primary,
                                                    fontSize: '0.72rem', fontWeight: '800', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '3px'
                                                }}
                                            >
                                                Perfil <ArrowRight size={11} />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    /* Empty State */
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        justifyContent: 'center', textAlign: 'center', padding: '30px 16px',
                                        flex: 1, gap: '14px'
                                    }}>
                                        <div style={{
                                            width: '60px', height: '60px', borderRadius: '18px',
                                            background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}20)`,
                                            border: `1.5px solid ${colors.secondary}35`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: isDark ? '#ffd97c' : colors.primary
                                        }}>
                                            <Sparkles size={28} />
                                        </div>
                                        <div>
                                            <p style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '6px' }}>
                                                ¡Tu agenda está libre!
                                            </p>
                                            <p style={{ fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#64748b', maxWidth: '300px', lineHeight: 1.5 }}>
                                                Reserva un escritorio, sala ejecutiva o espacio de eventos cuando lo necesites en Co Marca.
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => navigate(ROUTES.reservaYa)}
                                            style={{
                                                marginTop: '6px',
                                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                                padding: '11px 22px', borderRadius: '12px',
                                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                border: 'none', color: '#fff',
                                                fontSize: '0.84rem', fontWeight: '800',
                                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                boxShadow: '0 4px 16px rgba(97, 37, 123, 0.4)',
                                                transition: 'all 0.2s ease',
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                            }}
                                        >
                                            <CalendarPlus size={16} />
                                            Reservar Espacio Ahora
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* ── COLUMNA 2: CALENDARIO MENSUAL INTERACTIVO ── */}
                            <div style={{
                                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                background: isDark ? 'rgba(15, 7, 28, 0.65)' : '#f8fafc',
                                borderRadius: '20px',
                                border: isDark ? '1.5px solid rgba(193, 52, 139, 0.2)' : '1.5px solid #eef2f6',
                                padding: 'clamp(18px, 2.5vw, 24px)',
                            }}>
                                {/* Header del Calendario */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', gap: '10px', flexWrap: 'wrap' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            width: '36px', height: '36px', borderRadius: '10px',
                                            background: `${colors.secondary}15`, border: `1.5px solid ${colors.secondary}30`,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: colors.secondary
                                        }}>
                                            <Calendar size={18} />
                                        </div>
                                        <div>
                                            <h4 style={{
                                                fontSize: '1rem', fontWeight: '800',
                                                color: isDark ? '#ffffff' : '#0f172a',
                                                margin: 0, textTransform: 'capitalize'
                                            }}>
                                                {calendarDate.toLocaleDateString('es-BO', { month: 'long', year: 'numeric' })}
                                            </h4>
                                            <p style={{ fontSize: '0.72rem', color: isDark ? '#94a3b8' : '#64748b', margin: 0, fontWeight: '500' }}>
                                                {totalDiasConReservaMes > 0 ? `${totalDiasConReservaMes} ${totalDiasConReservaMes === 1 ? 'día con reserva' : 'días con reservas'} este mes` : 'Sin reservas en este mes'}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Navegación entre meses */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <button
                                            onClick={handleGoToday}
                                            title="Ir al mes actual"
                                            style={{
                                                padding: '5px 10px', borderRadius: '8px',
                                                background: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
                                                border: isDark ? '1px solid rgba(255, 255, 255, 0.15)' : '1px solid #cbd5e1',
                                                color: isDark ? '#ffd97c' : colors.primary,
                                                fontSize: '0.72rem', fontWeight: '700', cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            Hoy
                                        </button>
                                        <button
                                            onClick={handlePrevMonth}
                                            aria-label="Mes anterior"
                                            style={{
                                                width: '30px', height: '30px', borderRadius: '8px',
                                                background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#ffffff',
                                                border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #cbd5e1',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: isDark ? '#e2e8f0' : '#475569', cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <button
                                            onClick={handleNextMonth}
                                            aria-label="Mes siguiente"
                                            style={{
                                                width: '30px', height: '30px', borderRadius: '8px',
                                                background: isDark ? 'rgba(255, 255, 255, 0.06)' : '#ffffff',
                                                border: isDark ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid #cbd5e1',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                color: isDark ? '#e2e8f0' : '#475569', cursor: 'pointer',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>

                                {/* Encabezado de Días de la Semana */}
                                <div style={{
                                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '6px', textAlign: 'center', marginBottom: '8px'
                                }}>
                                    {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((d, i) => (
                                        <span key={d} style={{
                                            fontSize: '0.68rem', fontWeight: '800',
                                            color: i >= 5 ? (isDark ? '#f472b6' : colors.secondary) : (isDark ? '#94a3b8' : '#64748b'),
                                            textTransform: 'uppercase', letterSpacing: '0.04em'
                                        }}>
                                            {d}
                                        </span>
                                    ))}
                                </div>

                                {/* Matriz de Días del Mes */}
                                <div style={{
                                    display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                                    gap: '6px', marginBottom: '16px'
                                }}>
                                    {calendarGrid.map((cell) => {
                                        const tieneReserva = cell.citas.length > 0;
                                        const isSelected = selectedCalendarDate === cell.dateStr;
                                        const isProxima = proximaCita?.fecha_inicio?.startsWith(cell.dateStr);

                                        return (
                                            <button
                                                key={cell.dateStr}
                                                onClick={() => setSelectedCalendarDate(isSelected ? null : cell.dateStr)}
                                                style={{
                                                    aspectRatio: '1',
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    borderRadius: '11px',
                                                    border: isSelected
                                                        ? `2px solid ${colors.secondary}`
                                                        : isProxima
                                                        ? `1.5px solid ${isDark ? '#ffd97c' : colors.primary}`
                                                        : cell.isToday
                                                        ? `1.5px solid ${isDark ? '#34d399' : '#059669'}`
                                                        : tieneReserva
                                                        ? `1px solid ${isDark ? 'rgba(193, 52, 139, 0.45)' : 'rgba(193, 52, 139, 0.3)'}`
                                                        : '1px solid transparent',
                                                    background: isSelected
                                                        ? (isDark ? 'rgba(193, 52, 139, 0.3)' : `${colors.secondary}18`)
                                                        : tieneReserva
                                                        ? (isDark ? 'rgba(193, 52, 139, 0.18)' : `${colors.secondary}0d`)
                                                        : cell.isToday
                                                        ? (isDark ? 'rgba(52, 211, 153, 0.12)' : '#ecfdf5')
                                                        : 'transparent',
                                                    color: !cell.isCurrentMonth
                                                        ? (isDark ? '#4b5563' : '#cbd5e1')
                                                        : tieneReserva
                                                        ? (isDark ? '#ffffff' : colors.primaryDark)
                                                        : cell.isToday
                                                        ? (isDark ? '#34d399' : '#059669')
                                                        : (isDark ? '#f1f5f9' : '#1e293b'),
                                                    fontSize: '0.82rem',
                                                    fontWeight: tieneReserva || cell.isToday || isSelected ? '800' : '600',
                                                    cursor: 'pointer',
                                                    fontFamily: "'DM Sans', sans-serif",
                                                    padding: '2px',
                                                    transition: 'all 0.18s cubic-bezier(.16,1,.3,1)',
                                                }}
                                                onMouseEnter={e => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.transform = 'scale(1.08)';
                                                        e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(97,37,123,0.06)';
                                                    }
                                                }}
                                                onMouseLeave={e => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.background = tieneReserva
                                                            ? (isDark ? 'rgba(193, 52, 139, 0.18)' : `${colors.secondary}0d`)
                                                            : cell.isToday
                                                            ? (isDark ? 'rgba(52, 211, 153, 0.12)' : '#ecfdf5')
                                                            : 'transparent';
                                                    }
                                                }}
                                            >
                                                <span>{cell.dayNum}</span>
                                                
                                                {/* Indicador de reserva en la fecha */}
                                                {tieneReserva && (
                                                    <span style={{
                                                        position: 'absolute', bottom: '3px',
                                                        width: '5px', height: '5px', borderRadius: '50%',
                                                        background: isProxima ? (isDark ? '#ffd97c' : colors.accent) : colors.secondary,
                                                        boxShadow: `0 0 6px ${colors.secondary}`,
                                                    }} />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Panel inferior de Detalle del Día Seleccionado */}
                                <div style={{
                                    borderTop: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                                    paddingTop: '14px', marginTop: 'auto',
                                }}>
                                    {selectedCalendarDate && citasFechaSeleccionada.length > 0 ? (
                                        <div style={{ animation: 'fadeIn 0.25s ease' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <span style={{
                                                    fontSize: '0.72rem', fontWeight: '800',
                                                    color: isDark ? '#ffd97c' : colors.primary,
                                                    textTransform: 'uppercase', letterSpacing: '0.06em'
                                                }}>
                                                    📅 {selectedCalendarDate} · {citasFechaSeleccionada.length} {citasFechaSeleccionada.length === 1 ? 'Reserva' : 'Reservas'}
                                                </span>
                                                <button
                                                    onClick={() => setSelectedCalendarDate(null)}
                                                    style={{
                                                        background: 'transparent', border: 'none',
                                                        color: isDark ? '#94a3b8' : '#64748b',
                                                        fontSize: '0.7rem', cursor: 'pointer', fontWeight: '600'
                                                    }}
                                                >
                                                    Cerrar
                                                </button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
                                                {citasFechaSeleccionada.map(c => {
                                                    const p = planes.find(x => x.id === c.precio_plan_servicio);
                                                    return (
                                                        <div key={c.id} style={{
                                                            padding: '8px 12px', borderRadius: '10px',
                                                            background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
                                                            border: isDark ? '1px solid rgba(193, 52, 139, 0.25)' : '1px solid #e2e8f0',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px'
                                                        }}>
                                                            <div style={{ minWidth: 0 }}>
                                                                <p style={{ fontSize: '0.8rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    {c.reglas_plan_reserva?.is_event ? <><Ticket size={12} /> Evento: {c.reglas_plan_reserva?.evento_titulo}</> : (c.servicio_info?.nombre || p?.servicio_nombre || 'Servicio')}
                                                                </p>
                                                                <p style={{ fontSize: '0.7rem', color: isDark ? '#cbd5e1' : '#64748b', margin: '2px 0 0' }}>
                                                                    {c.reglas_plan_reserva?.is_event ? 'Reserva de Evento' : (c.hora_inicio ? `${formatHoraCorta(c.hora_inicio)} a ${formatHoraCorta(c.hora_fin)}` : 'Horario flexible')}
                                                                </p>
                                                            </div>
                                                            <button
                                                                onClick={() => navigate(`/mis-citas/${c.id}/detalles`)}
                                                                style={{
                                                                    padding: '4px 10px', borderRadius: '6px',
                                                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                                    border: 'none', color: '#fff', fontSize: '0.68rem', fontWeight: '700',
                                                                    cursor: 'pointer', flexShrink: 0
                                                                }}
                                                            >
                                                                Ver
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : selectedCalendarDate && citasFechaSeleccionada.length === 0 ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', animation: 'fadeIn 0.2s ease' }}>
                                            <div>
                                                <p style={{ fontSize: '0.75rem', fontWeight: '700', color: isDark ? '#e2e8f0' : '#334155', margin: 0 }}>
                                                    {selectedCalendarDate} · Sin reservas
                                                </p>
                                                <p style={{ fontSize: '0.68rem', color: isDark ? '#94a3b8' : '#64748b', margin: '2px 0 0' }}>
                                                    Espacios y salas disponibles para reservar.
                                                </p>
                                            </div>
                                            <button
                                                onClick={() => navigate(ROUTES.reservaYa)}
                                                style={{
                                                    padding: '5px 10px', borderRadius: '7px',
                                                    background: `${colors.primary}18`, border: `1px solid ${colors.primary}40`,
                                                    color: isDark ? '#ffd97c' : colors.primary,
                                                    fontSize: '0.7rem', fontWeight: '800', cursor: 'pointer',
                                                    display: 'inline-flex', alignItems: 'center', gap: '4px'
                                                }}
                                            >
                                                <CalendarPlus size={12} /> Reservar
                                            </button>
                                        </div>
                                    ) : (
                                        /* Leyenda y Acceso Rápido por defecto */
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>
                                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: colors.secondary, display: 'inline-block' }} />
                                                    Reserva
                                                </span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>
                                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: isDark ? '#ffd97c' : colors.accent, display: 'inline-block' }} />
                                                    Próxima
                                                </span>
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.68rem', color: isDark ? '#cbd5e1' : '#64748b', fontWeight: '600' }}>
                                                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: isDark ? '#34d399' : '#059669', display: 'inline-block' }} />
                                                    Hoy
                                                </span>
                                            </div>
                                            <button
                                                onClick={() => navigate(ROUTES.reservaYa)}
                                                style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                                                    padding: '5px 12px', borderRadius: '8px',
                                                    background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                                    border: 'none', color: '#fff', fontSize: '0.72rem', fontWeight: '800',
                                                    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                    boxShadow: '0 2px 8px rgba(97, 37, 123, 0.3)',
                                                    transition: 'all 0.15s ease',
                                                }}
                                            >
                                                <CalendarPlus size={13} />
                                                Nueva Reserva
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Section>

                {/* ══ HISTORIAL TABS ══ */}
                <Section delay={0.15}>
                    {/* Tab switcher */}
                    <div id="historial-seccion" style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        {([
                            { id: 'reservas' as const, label: 'Historial de Reservas', Icon: Calendar, color: isDark ? '#ffd97c' : colors.primary, badge: undefined as number | undefined },
                            { id: 'correspondencia' as const, label: 'Correspondencia', Icon: MessageSquare, color: isDark ? '#f472b6' : colors.secondary, badge: citasConCorrespondencia.length as number | undefined },
                        ]).map(({ id, label, Icon, color, badge }) => (
                            <button
                                key={id}
                                className="tab-btn"
                                onClick={() => setTabPrincipalActiva(id)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                                    padding: '10px 20px', borderRadius: '12px',
                                    border: tabPrincipalActiva === id
                                        ? `1.5px solid ${color}50`
                                        : (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #f1f5f9'),
                                    background: tabPrincipalActiva === id
                                        ? `${color}15`
                                        : (isDark ? 'rgba(22, 10, 43, 0.7)' : '#fff'),
                                    fontSize: '0.85rem', fontWeight: '700',
                                    color: tabPrincipalActiva === id
                                        ? color
                                        : (isDark ? '#cbd5e1' : '#64748b'),
                                    cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                    boxShadow: tabPrincipalActiva === id ? `0 4px 16px ${color}20` : 'none',
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                <div style={{
                                    width: '26px', height: '26px', borderRadius: '7px',
                                    background: tabPrincipalActiva === id
                                        ? `linear-gradient(145deg, ${color}cc, ${color})`
                                        : `${color}12`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: tabPrincipalActiva === id ? '#fff' : color,
                                }}>
                                    <Icon size={14} />
                                </div>
                                {label}
                                {badge !== undefined && badge > 0 && (
                                    <span style={{
                                        background: color, color: '#fff',
                                        fontSize: '0.65rem', fontWeight: '800',
                                        borderRadius: '100px', padding: '2px 7px',
                                    }}>{badge}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Contenido */}
                    <div style={{
                        background: isDark ? 'rgba(22, 10, 43, 0.85)' : '#fff',
                        borderRadius: '24px',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9',
                        boxShadow: isDark ? '0 8px 30px rgba(0,0,0,0.3)' : '0 2px 16px rgba(0,0,0,0.04)',
                        backdropFilter: 'blur(12px)',
                    }}>

                        <div style={{ padding: '28px' }}>
                            {/* Header de sección */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' }}>
                                <SectionTitle
                                    label={tabPrincipalActiva === 'reservas' ? 'Reservas' : 'Mensajes'}
                                    title={tabPrincipalActiva === 'reservas' ? 'Historial de Reservas' : 'Historial de Correspondencia'}
                                    accent={tabPrincipalActiva === 'reservas' ? colors.primary : colors.secondary}
                                    sub={tabPrincipalActiva === 'reservas'
                                        ? `${citasFiltradas.length} reservas encontradas`
                                        : `${citasConCorrespondencia.length} citas con correspondencia`}
                                />

                                {/* Búsqueda Responsiva */}
                                <div style={{ position: 'relative', width: '100%', maxWidth: '260px' }} className="dashboard-search-container">
                                    <Search size={14} color={searchFocused ? colors.secondary : '#94a3b8'} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', transition: 'color 0.2s' }} />
                                    <input
                                        type="text"
                                        placeholder={tabPrincipalActiva === 'reservas' ? 'Código, servicio...' : 'Código de cita...'}
                                        value={busqueda}
                                        onChange={e => setBusqueda(e.target.value)}
                                        onFocus={() => setSearchFocused(true)}
                                        onBlur={() => setSearchFocused(false)}
                                        style={{
                                            padding: '9px 16px 9px 34px',
                                            borderRadius: '12px',
                                            border: searchFocused
                                                ? `1.5px solid ${colors.secondary}60`
                                                : (isDark ? '1.5px solid rgba(255, 255, 255, 0.1)' : '1.5px solid #f1f5f9'),
                                            background: isDark ? 'rgba(15, 7, 28, 0.85)' : '#f8fafc',
                                            fontSize: '0.83rem',
                                            color: isDark ? '#f1f5f9' : '#374151',
                                            fontFamily: "'DM Sans', sans-serif",
                                            outline: 'none', width: '100%',
                                            boxShadow: searchFocused ? `0 0 0 3px ${colors.secondary}20` : 'none',
                                            transition: 'all 0.2s',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Sub-tabs solo para reservas */}
                            {tabPrincipalActiva === 'reservas' && (
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                    {([
                                        { id: 'todas' as const, label: `Todas (${citas.length})`, color: undefined as string | undefined },
                                        { id: 'titular' as const, label: `Titular (${stats.totalCitasTitular})`, color: (isDark ? '#ffd97c' : colors.primary) as string | undefined },
                                        { id: 'asociadas' as const, label: `Asociado (${stats.totalCitasAsociadas})`, color: (isDark ? '#f472b6' : colors.secondary) as string | undefined },
                                    ]).map(({ id, label, color }) => (
                                        <button
                                            key={id}
                                            onClick={() => setTabActiva(id)}
                                            style={{
                                                padding: '7px 16px', borderRadius: '10px',
                                                border: tabActiva === id
                                                    ? `1.5px solid ${(color ?? '#94a3b8') + '50'}`
                                                    : (isDark ? '1.5px solid rgba(255, 255, 255, 0.08)' : '1.5px solid #f1f5f9'),
                                                background: tabActiva === id
                                                    ? `${(color ?? '#64748b') + '20'}`
                                                    : (isDark ? 'rgba(15, 7, 28, 0.6)' : '#f8fafc'),
                                                fontSize: '0.78rem', fontWeight: '700',
                                                color: tabActiva === id ? (color ?? (isDark ? '#fff' : '#374151')) : (isDark ? '#94a3b8' : '#64748b'),
                                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                transition: 'all 0.2s',
                                            }}
                                        >{label}</button>
                                    ))}
                                </div>
                            )}

                            {/* Lista de citas — paginada */}
                            {tabPrincipalActiva === 'reservas' && (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {citasPaginadas.length > 0 ? citasPaginadas.map((cita, idx) => (
                                            <CitaRow
                                                key={cita.id} cita={cita} index={idx}
                                                plan={planes.find(p => p.id === cita.precio_plan_servicio)}
                                                rol={determinarRolCita(cita)}
                                                onVerSesiones={() => navigate(`/mis-citas/${cita.id}/sesiones`)}
                                                onVerPagos={() => { setCitaSeleccionada(cita); setModalListadoPagosOpen(true); }}
                                            />
                                        )) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: '14px' }}>
                                                <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: `${colors.primary}08`, border: `1.5px solid ${colors.primary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Calendar size={28} color={colors.primary} strokeWidth={1.5} />
                                                </div>
                                                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: isDark ? '#e2e8f0' : '#374151' }}>No hay reservas{busqueda ? ' con esa búsqueda' : ''}</p>
                                                <p style={{ fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>Intenta con otros filtros o términos</p>
                                            </div>
                                        )}
                                    </div>
                                    <PaginationControls
                                        paginaActual={paginaActual}
                                        totalPaginas={totalPaginasReservas}
                                        totalItems={citasFiltradas.length}
                                        itemsPorPagina={ITEMS_POR_PAGINA}
                                        onPaginaChange={setPaginaActual}
                                        isDark={isDark}
                                    />
                                </div>
                            )}

                            {/* Lista de correspondencia — paginada */}
                            {tabPrincipalActiva === 'correspondencia' && (
                                <div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {corrPaginada.length > 0 ? corrPaginada.map((cita) => {
                                            const plan = planes.find(p => p.id === cita.precio_plan_servicio);
                                            const rol = determinarRolCita(cita);
                                            return (
                                                <CorrespondenciaItem
                                                    key={cita.id}
                                                    cita={cita}
                                                    plan={plan}
                                                    rol={rol}
                                                    onVerHistorial={() => {
                                                        setCitaSeleccionadaCorrespondencia(cita);
                                                        setModalHistorialCorrespondenciaOpen(true);
                                                    }}
                                                />
                                            );
                                        }) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px', gap: '14px' }}>
                                                <div style={{ width: '60px', height: '60px', borderRadius: '18px', background: `${colors.secondary}08`, border: `1.5px solid ${colors.secondary}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <MessageSquare size={28} color={colors.secondary} strokeWidth={1.5} />
                                                </div>
                                                <p style={{ fontSize: '0.95rem', fontWeight: '700', color: isDark ? '#e2e8f0' : '#374151' }}>Sin correspondencia habilitada</p>
                                                <p style={{ fontSize: '0.82rem', color: isDark ? '#94a3b8' : '#94a3b8', fontWeight: '500' }}>No tienes citas con correspondencia activa</p>
                                            </div>
                                        )}
                                    </div>
                                    <PaginationControls
                                        paginaActual={paginaCorr}
                                        totalPaginas={totalPaginasCorr}
                                        totalItems={citasConCorrespondencia.length}
                                        itemsPorPagina={ITEMS_POR_PAGINA}
                                        onPaginaChange={setPaginaCorr}
                                        isDark={isDark}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </Section>
            </div>

            {/* Modales */}
            {modalListadoPagosOpen && citaSeleccionada && (
                <PagosModalHistorial
                    isOpen={modalListadoPagosOpen}
                    onClose={() => setModalListadoPagosOpen(false)}
                    citaId={citaSeleccionada.id}
                />
            )}
            {modalHistorialCorrespondenciaOpen && citaSeleccionadaCorrespondencia && (
                <HistorialCorrespondenciaUsuarios
                    isOpen={modalHistorialCorrespondenciaOpen}
                    onClose={() => setModalHistorialCorrespondenciaOpen(false)}
                    citaId={citaSeleccionadaCorrespondencia.id}
                    citaCodigo={citaSeleccionadaCorrespondencia.codigo}
                />
            )}

            {/* Modal de Acceso Wi-Fi Rápido */}
            {modalWifiOpen && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px',
                    animation: 'fadeIn 0.2s ease',
                }}>
                    <div style={{
                        background: isDark ? 'linear-gradient(145deg, #180a30 0%, #290e48 100%)' : '#ffffff',
                        border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : '1.5px solid #e2e8f0',
                        borderRadius: '24px', padding: '32px',
                        maxWidth: '440px', width: '100%',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
                        fontFamily: "'DM Sans', sans-serif",
                        position: 'relative',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '22px' }}>
                            <div style={{
                                width: '48px', height: '48px', borderRadius: '14px',
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff'
                            }}>
                                <Wifi size={24} />
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a', margin: 0 }}>
                                    Red Wi-Fi Cowork
                                </h3>
                                <p style={{ fontSize: '0.8rem', color: isDark ? '#94a3b8' : '#64748b', margin: '2px 0 0' }}>
                                    Conectividad de alta velocidad para miembros
                                </p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
                            <div style={{
                                background: isDark ? 'rgba(0,0,0,0.35)' : '#f8fafc',
                                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                                borderRadius: '14px', padding: '14px 16px'
                            }}>
                                <p style={{ fontSize: '0.7rem', fontWeight: '700', color: isDark ? '#ffd97c' : colors.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                                    Nombre de Red (SSID)
                                </p>
                                <p style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', fontFamily: 'monospace', margin: 0 }}>
                                    CoMarca_Members_5G
                                </p>
                            </div>

                            <div style={{
                                background: isDark ? 'rgba(0,0,0,0.35)' : '#f8fafc',
                                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0',
                                borderRadius: '14px', padding: '14px 16px',
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px'
                            }}>
                                <div>
                                    <p style={{ fontSize: '0.7rem', fontWeight: '700', color: isDark ? '#ffd97c' : colors.primary, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                                        Contraseña
                                    </p>
                                    <p style={{ fontSize: '1.05rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', fontFamily: 'monospace', margin: 0 }}>
                                        comarcavip2024
                                    </p>
                                </div>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText('comarcavip2024');
                                        setWifiCopiado(true);
                                        setTimeout(() => setWifiCopiado(false), 2200);
                                    }}
                                    style={{
                                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                                        padding: '8px 14px', borderRadius: '10px',
                                        background: wifiCopiado ? '#10b981' : `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                                        border: 'none', color: '#fff', fontSize: '0.78rem', fontWeight: '800',
                                        cursor: 'pointer', transition: 'all 0.2s',
                                    }}
                                >
                                    {wifiCopiado ? <Check size={14} /> : <Copy size={14} />}
                                    {wifiCopiado ? '¡Copiado!' : 'Copiar'}
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={() => setModalWifiOpen(false)}
                            style={{
                                width: '100%', padding: '12px', borderRadius: '14px',
                                background: isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9',
                                border: isDark ? '1px solid rgba(255,255,255,0.15)' : '1px solid #e2e8f0',
                                color: isDark ? '#ffffff' : '#0f172a', fontSize: '0.85rem', fontWeight: '800',
                                cursor: 'pointer', transition: 'all 0.2s',
                            }}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MiDashboard;

